/**
 * COM-7.2 — the operator's readers for pay periods.
 *
 * ⚠️ EVERY FUNCTION HERE THROWS. NOTHING RETURNS AN EMPTY LIST ON FAILURE.
 * Same rule as `commissionEarningsService.ts`, and it matters more here: this
 * screen already has to distinguish "no periods" from "you may not see periods"
 * (probe P6, 2026-08-25 — an unauthorised caller gets zero rows, not an error).
 * Adding a third indistinguishable empty for "the request failed" would make the
 * distinction unrecoverable. One `catch { return [] }` tells an operator their
 * account has no payroll during an outage.
 *
 * ⚠️ NOTHING HERE COMPUTES A FIGURE. Four readers, probed live before this file
 * was written, each returning amounts already summed by the same code that
 * writes the ledger.
 */

import { supabase } from '@/lib/supabaseClient';
import { type ApprovalRow, type ApprovalScope, approvalArgs } from '@/lib/commissionApproval';
import type { ExportRow, PayBatchRow } from '@/lib/commissionPayout';
import type {
  BulkMode,
  BulkSummaryRow,
  CorrectionHistoryRow,
  CorrectionRow,
} from '@/lib/commissionCorrection';
import type {
  OwedByPeriodRow,
  PayPeriodRow,
  PeriodLineRow,
  PeriodSubjectRow,
  PeriodSummaryRow,
} from '@/lib/commissionPayPeriods';

function unwrap<T>(what: string, data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(`${what}: ${error.message}`);
  return (data ?? []) as T;
}

/**
 * Every pay period in the account.
 *
 * ⚠️ READ FROM THE TABLE, NOT FROM `commission_owed_by_period()`, AND THAT IS
 * DELIBERATE. The totals reader returns a row per period THAT HAS LINES, so a
 * period that has just opened would be missing from a list driven by it — the
 * operator would not see the period their next payroll lands in until something
 * landed in it. RLS on `commission_pay_periods` scopes the select to
 * `merged_scope_tenant_ids()`, so this is not a widening.
 */
export async function getPayPeriods(): Promise<PayPeriodRow[]> {
  const { data, error } = await supabase
    .from('commission_pay_periods')
    .select(
      'id, period_key, period_label, period_start, period_end, cadence, is_provisional, state, approved_at, paid_at, paid_on, payment_reference',
    )
    .order('period_start', { ascending: false });
  return unwrap('Could not load pay periods', data as PayPeriodRow[] | null, error);
}

/**
 * Totals for every period at once.
 *
 * ⚠️ ONE CALL FOR THE WHOLE LIST, NOT ONE PER PERIOD. `p_period_ids` is left
 * null so the reader answers for all of them; a per-period loop would be N+1
 * round trips for a screen whose entire purpose is the overview.
 *
 * The remaining ten parameters are filters this story does not use. They are the
 * seam COM-7.3 and COM-7.6 will need, and are left at their defaults rather than
 * spelled out, so a default changing in the database is not silently overridden
 * here.
 */
export async function getOwedByPeriod(tenantId: string): Promise<OwedByPeriodRow[]> {
  const { data, error } = await supabase.rpc('commission_owed_by_period', {
    p_tenant_id: tenantId,
  });
  return unwrap('Could not load pay period totals', data as OwedByPeriodRow[] | null, error);
}

/**
 * One period's header figures.
 *
 * ⚠️ RETURNS null WHEN THE READER GAVE NOTHING, AND THAT IS NOT "NO SUCH
 * PERIOD". `commission_period_summary()` checks `commission.view` itself and
 * answers with an empty set when the caller lacks it, so an absent row means
 * either "no such period" or "not yours to see". The screen must not phrase it
 * as the former.
 */
export async function getPeriodSummary(periodId: string): Promise<PeriodSummaryRow | null> {
  const { data, error } = await supabase.rpc('commission_period_summary', {
    p_period_id: periodId,
  });
  if (error) throw new Error(`Could not load this pay period: ${error.message}`);
  const rows = (data as PeriodSummaryRow[] | null) ?? [];
  return rows[0] ?? null;
}

/** Every subject with money in one period, and each subject's own approval state. */
export async function getPeriodSubjects(periodId: string): Promise<PeriodSubjectRow[]> {
  const { data, error } = await supabase.rpc('commission_period_review', {
    p_period_id: periodId,
  });
  return unwrap('Could not load the people in this period', data as PeriodSubjectRow[] | null, error);
}

/**
 * One subject's lines within a period.
 *
 * ⚠️ BOTH SUBJECT ARGUMENTS ARE PASSED, INCLUDING THE null ONE. A commission
 * subject is a person or an org, never both, and the reader matches on the pair.
 * Omitting the null half lets PostgREST fall back to a default and match the
 * wrong subject.
 */
export async function getPeriodLines(
  periodId: string,
  subjectUserId: string | null,
  subjectOrgId: string | null,
): Promise<PeriodLineRow[]> {
  const { data, error } = await supabase.rpc('commission_period_review_lines', {
    p_period_id: periodId,
    p_subject_user_id: subjectUserId,
    p_subject_org_id: subjectOrgId,
  });
  return unwrap('Could not load the lines for this person', data as PeriodLineRow[] | null, error);
}

/**
 * COM-7.3 — preview an approval without performing it.
 *
 * ⚠️ THE DRY RUN GENUINELY WRITES NOTHING. Measured 2026-08-26: 8 pending
 * lines before the call and 8 after, period still `open`. That is worth having
 * checked rather than assumed, because the whole preview-then-commit pattern
 * rests on it.
 *
 * ⚠️ ZERO ROWS IS A LEGITIMATE ANSWER AND IT DOES NOT MEAN "NOTHING WILL
 * HAPPEN". A whole-batch approve of a period with nothing approvable previews
 * as an empty list and still closes the period on commit. `describeApproval()`
 * is what says so; this function must not swallow the empty case.
 */
export async function previewApprovePeriod(
  periodId: string,
  scope: ApprovalScope,
): Promise<ApprovalRow[]> {
  const { data, error } = await supabase.rpc(
    'commission_approve_period',
    approvalArgs(periodId, scope, true),
  );
  return unwrap('Could not preview this approval', data as ApprovalRow[] | null, error);
}

/**
 * COM-7.3 — approve, for real.
 *
 * ⚠️ THE DATABASE'S REFUSALS ARE GOOD COPY AND ARE PASSED THROUGH, NOT
 * REPLACED. Measured wordings include "requires commission.approve on the
 * account that owns it. Viewing it does not." (42501), "is already approved, so
 * there is nothing to approve" (23514) and the one explaining that closing a
 * batch while approving named contributors would leave unapproved lines inside
 * it. Each names the thing to do next, which is exactly what COM-6.5's refusal
 * copy had to be fixed to do. Wrapping them in "Approval failed" would throw
 * that away.
 */
export async function approvePeriod(
  periodId: string,
  scope: ApprovalScope,
): Promise<ApprovalRow[]> {
  const { data, error } = await supabase.rpc(
    'commission_approve_period',
    approvalArgs(periodId, scope, false),
  );
  if (error) throw new Error(error.message);
  return (data as ApprovalRow[] | null) ?? [];
}

/**
 * Does the CALLER hold this commission permission on this account, as the
 * database itself resolves it?
 *
 * ⚠️ THIS EXISTS BECAUSE THE CLIENT'S PERMISSION CONTEXT AND THE DATABASE
 * DISAGREE, AND THE DATABASE IS THE ONE THAT DECIDES.
 *
 * Measured 2026-08-26 against the live account. `cost_holds_permission()`, the
 * body behind every commission authority check, resolves a permission through
 * `users.role_id` OR `user_roles`. `PermissionContext.tsx` uses an if/else:
 * "if user has a role_id, use the legacy system", and never consults
 * `user_roles` for such a user. The test account holds `system.override` only
 * through `user_roles`, so the database grants it every commission authority
 * while `hasPermission('system.override')` in the client answers false.
 *
 * The consequence on this screen was an approve control that never appeared for
 * a user who is allowed to approve. Gating on this RPC instead makes the
 * courtesy check agree with the enforcement by construction rather than by a
 * mirror somebody has to keep in step.
 *
 * ⚠️ THE WIDER DIVERGENCE IS NOT FIXED HERE. Every other client permission
 * check in the product still runs through `PermissionContext`, and changing how
 * that resolver works would alter authority across every screen at once. That
 * is a decision, not a tidy-up.
 */
export async function hasCommissionAuthority(
  permission: string,
  tenantId: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('commission_has_authority', {
    p_permission: permission,
    p_tenant_id: tenantId,
  });
  // ⚠️ An error is NOT "no authority". Answering false on a failed request
  // hides a control from somebody who holds the permission and gives them no
  // way to tell a denial from an outage.
  if (error) throw new Error(`Could not check commission permissions: ${error.message}`);
  return data === true;
}

/**
 * COM-7.5 — pay a batch, or preview what paying would do.
 *
 * ⚠️ `commission.pay`, NOT `commission.approve`. Both batch functions are
 * SECURITY DEFINER and check `commission.pay` themselves, so the
 * `commission_pay_periods` write policy naming `commission.approve` is not what
 * governs paying. That was measured and nothing needs fixing; do not
 * re-litigate it from the policy.
 *
 * ⚠️ A DRY RUN IS NOT A PROMISE THE COMMIT WILL WORK. A period that has not
 * started previews successfully with `outcome = 'would_pay_not_yet'` and then
 * raises 23514 on the real run. `payBlockedReason()` is what keeps the two
 * apart; this function must not swallow the distinction.
 *
 * ⚠️ THE REAL RUN CAN WRITE AN EXPORT BY ITSELF. If the last export's pinned
 * recovery no longer matches what this run recovers, `commission_pay_batch`
 * calls `commission_export_batch(id, false, true)` internally. Re-read the
 * exports after paying rather than assuming they are unchanged.
 */
export async function payBatch(
  periodId: string,
  paidOn: string | null,
  reference: string | null,
  dryRun: boolean,
): Promise<PayBatchRow[]> {
  const { data, error } = await supabase.rpc('commission_pay_batch', {
    p_period_id: periodId,
    p_paid_on: paidOn,
    p_reference: reference,
    p_dry_run: dryRun,
  });
  // ⚠️ The database's refusals name what to do next -- "Approve it first
  // (COM-4.3)", "Paid is terminal; a correction lands forward (COM-4.10)",
  // "Approving it is a different authority". Passed through, not replaced.
  if (error) throw new Error(error.message);
  return (data as PayBatchRow[] | null) ?? [];
}

/**
 * COM-7.5 — export a batch to payroll, or read what is already exported.
 *
 * ⚠️ THE THREE THINGS THIS CAN DO ARE NOT `dryRun ? preview : write`.
 * On a period that has already been exported, the already-exported branch
 * returns BEFORE the dry-run branch is reached, so a `dryRun = true` call
 * replays the PINNED ORIGINAL rows rather than recomputing anything. Measured:
 * the same `export_id` came back with "Content is the pinned original, not a
 * recomputation." Ask `exportMode()` what a given call will actually do, and
 * never label a pinned replay a preview.
 *
 * ⚠️ `p_supersede` ADDS AN EXPORT, IT DOES NOT REPLACE ONE. Measured: export
 * records went 1 -> 2. The earlier export stays as the record of what payroll
 * was actually sent.
 */
export async function exportBatch(
  periodId: string,
  dryRun: boolean,
  supersede: boolean,
): Promise<ExportRow[]> {
  const { data, error } = await supabase.rpc('commission_export_batch', {
    p_period_id: periodId,
    p_dry_run: dryRun,
    p_supersede: supersede,
  });
  if (error) throw new Error(error.message);
  return (data as ExportRow[] | null) ?? [];
}

/**
 * COM-7.6 — correct a line, or preview what correcting would do.
 *
 * ⚠️ `commission.adjust`, WHICH IS ITS OWN AUTHORITY. Not `commission.approve`
 * and not `commission.pay`. The database's refusal explains the model: "There
 * is no approval queue for a correction — the authority IS the permission."
 *
 * ⚠️ IT NEVER EDITS THE LINE. A real run writes a reversing contra, flips the
 * original's `validity` to `reversed`, and writes a replacement. Measured: 20
 * lines became 22 and the original still read 760.00 afterwards. Any caller
 * holding the old line id must expect it to be reversed, not changed.
 *
 * ⚠️ THE DIFFERENCE MAY LAND IN A DIFFERENT PERIOD, AND FOR A PAID LINE IT
 * ALWAYS DOES — measured `month:2025-11` correcting into `month:2025-12`, in a
 * period that did not exist yet. `landsForward()` is what makes a screen say so.
 */
export async function correctLine(
  lineId: string,
  newAmount: string,
  reason: string,
  dryRun: boolean,
): Promise<CorrectionRow[]> {
  const { data, error } = await supabase.rpc('commission_correct_line', {
    p_line_id: lineId,
    p_new_amount: newAmount,
    p_reason: reason,
    p_dry_run: dryRun,
  });
  // ⚠️ THREE DIFFERENT REFUSALS SHARE 23514 HERE and each names a different
  // thing to do next -- "Correct the line that replaced it", "Correct the line
  // it reverses", "A correction that changes nothing...". Passed through.
  if (error) throw new Error(error.message);
  return (data as CorrectionRow[] | null) ?? [];
}

/**
 * COM-7.6 — the correction history.
 *
 * ⚠️ EITHER ID FINDS THE RECORD, AND NULL FINDS THEM ALL. Measured: the
 * ORIGINAL line id and the REPLACEMENT line id both return the same row, and
 * `p_line_id => NULL` returns every correction in scope. So one call can
 * annotate a whole line list, which is how the period screen uses it.
 */
export async function getLineCorrections(lineId?: string): Promise<CorrectionHistoryRow[]> {
  const { data, error } = await supabase.rpc('commission_line_corrections', {
    p_line_id: lineId ?? null,
    p_from: null,
    p_to: null,
  });
  if (error) throw new Error(`Could not load the correction history: ${error.message}`);
  return (data as CorrectionHistoryRow[] | null) ?? [];
}

/**
 * COM-7.6 — what a bulk correction would do, without doing it.
 *
 * ⚠️ THE TOTALS COVER ONLY THE CORRECTABLE LINES. Excluded lines are listed in
 * `excluded` with the database's own reason for each, which for a paid line
 * names `p_include_paid` and explains that the correction would then land in
 * the next OPEN period. Rendering only the counts throws that instruction away.
 */
export async function previewBulkCorrection(
  lineIds: readonly string[],
  mode: BulkMode,
  value: string,
  reason: string,
  includePaid: boolean,
): Promise<BulkSummaryRow[]> {
  const { data, error } = await supabase.rpc('commission_correct_lines_bulk_summary', {
    p_line_ids: lineIds,
    p_mode: mode,
    p_value: value,
    p_reason: reason,
    p_include_paid: includePaid,
  });
  if (error) throw new Error(error.message);
  return (data as BulkSummaryRow[] | null) ?? [];
}
